import React from "react";
import { useWindowSize } from "@shared/hooks/useWindowSize";
import { sectionSummary } from "./summaryModel";

/** Below Tailwind's `sm`, where the detail pages open with their summary. */
const PHONE_MAX_WIDTH = 639;

type FoldableSection = { header: React.ReactNode; items: Parameters<typeof sectionSummary>[0] };

/**
 * The GenericCard props of a service-detail card (CTX-1421). On a phone every
 * card starts folded under its title and a one-line summary, so the traffic
 * chart sits right under the page's summary instead of 1,000 to 1,800 px down.
 * Wider screens keep the card's own settings.
 */
export function usePhoneFold(
  section: FoldableSection,
  wide: { isCollapsible: boolean; initialExpanded?: boolean },
  /** A line of the card's own, when its rows are not label/value pairs. */
  summaryOverride?: string
): { isCollapsible: boolean; initialExpanded?: boolean; headerContent: React.ReactNode } {
  const isPhone = useWindowSize().width <= PHONE_MAX_WIDTH;
  if (!isPhone) return { ...wide, headerContent: section.header };

  const summary = summaryOverride ?? sectionSummary(section.items);
  return {
    isCollapsible: true,
    initialExpanded: false,
    headerContent: (
      // Capped to the screen less the card's gutters and chevron: the header row
      // does not shrink its title, so a long line pushed the chevron off.
      <span className="flex min-w-0 max-w-[calc(100vw-6.5rem)] flex-col">
        <span>{section.header}</span>
        {summary && (
          <span className="truncate text-xs font-normal text-semantic-text-secondary">{summary}</span>
        )}
      </span>
    ),
  };
}
